import {
  faDownload,
  faEllipsisVertical,
  faPlus,
  faTrash,
  faMagnifyingGlass,
  faFileLines,
  faFilePdf,
  faFileImage,
  faCalendarDays,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Form, Modal, Nav, Spinner } from "react-bootstrap";
import { useGetEssentials } from "../../Utils/hooks/useGetEssentials";
import { useGetRequest, usePostRequest } from "../../Utils/hooks/QueryHandler";
import { toast } from "sonner";
import { imageURL } from "../../Utils/constants/constant";
import { DeleteRequest } from "../../Utils/hooks/ApiHandler";
import DeleteModal from "../DeleteModal";
import { handleDownloadFile } from "../../Utils/helpers/commonhelper";
import { UnauthViewModal } from "../../CommonComponents/UnauthViewModal";

/**
 * ProjectDocument
 * -------------------------------------------------------------------------
 * Standalone "Documents" tab for the Project detail view, styled to match
 * the ProjectMeeting workspace: a left-hand scrollable list of documents
 * and a right-hand detail panel showing everything about the selected
 * document. Same data contract as the older `DocumentsList` component
 * (fetch / upload / delete), just rebuilt with the new two-pane layout.
 * -------------------------------------------------------------------------
 */

// Zod schema
const documentSchema = z.object({
  name: z.string().nonempty("Name is required"),
  // file: z
  //   .any()
  //   .refine((files) => {
  //     if (!files || files.length === 0) return false;
  //     const file = files[0];
  //     const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg","image/xls","image/xlsx","image/doc","image/docx"];
  //     return allowedTypes.includes(file.type);
  //   }, {
  //     message: "Only PDF, JPG, JPEG, and PNG files are allowed",
  //   }),
  file: z
  .any()
  .refine((files) => {
    if (!files || files.length === 0) return false;

    const file = files[0];

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    return allowedTypes.includes(file.type);
  }, {
    message:
      "Only PDF, JPG, JPEG, PNG, XLS, XLSX, DOC and DOCX files are allowed",
  }),
  description: z.string().nonempty("Description is required"),
});

const getFileExt = (path = "") => path.split(".").pop()?.toLowerCase() || "";

const isPdf = (path) => getFileExt(path) === "pdf";

const getFileTypeStyle = (path) =>
  isPdf(path)
    ? { bg: "rgba(220,53,69,0.1)", fg: "#dc3545", label: "PDF" }
    : { bg: "rgba(15,157,88,0.1)", fg: "#0f9d58", label: "Image" };

const formatedDate = (dates) => {
  if (!dates) return "";
  const date = new Date(dates);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);
};

const formatedTime = (times) => {
  if (!times) return "";
  const Time = new Date(times);
  return new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(Time);
};

const truncate = (str = "", n) =>
  str.length > n ? `${str.slice(0, n)}...` : str;

const ProjectDocument = ({ id, moduleType = "project", docGrant }) => {
  const [showModal, setShowModal] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [moduleName, setModuleName] = useState(moduleType || "");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const { headers, extras, customerId, loginUserId } = useGetEssentials();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(documentSchema) });

  const onClose = () => {
    setShowModal(false);
    reset();
  };

  //  Dynamic API URL for fetch
  const apiUrl =
    moduleType === "Company"
      ? `company/fetchDocuments/${customerId}/${id}/${loginUserId}`
      : moduleType === "project"
        ? `project/fetchDocuments/${customerId}/${id}/${loginUserId}`
        : `contacts/fetchDocuments/${customerId}/${id}/${loginUserId}`;

  const {
    data: documentData,
    error,
    refetch: fetchDocument,
    isError,
    isLoading,
  } = useGetRequest(apiUrl, "getDocument", headers, extras);

  // Update documents
  useEffect(() => {
    if (documentData?.status === "success") {
      setDocuments(documentData?.data || []);
    } else if (isError) {
      console.error("Error fetching documents:", error);
    }
  }, [documentData, isError, error]);

  // Update module name on prop change
  useEffect(() => {
    setModuleName(moduleType);
    fetchDocument();
  }, [moduleType, id]);

  // Keep a valid selection whenever the document list changes
  useEffect(() => {
    if (!documents?.length) {
      setSelectedDocumentId(null);
      return;
    }
    if (!documents.some((d) => d.id === selectedDocumentId)) {
      setSelectedDocumentId(documents[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents]);

  //  Dynamic upload API
  const { mutate: addDocument } = usePostRequest(
    `${moduleType.toLowerCase()}/uploadDocument/`,
    true,
    { headers },
    extras
  );

  // Form submit
  const onSubmit = async (formData) => {
    const file = formData.file?.[0];
    const payload = {
      [moduleType.toLowerCase() === "contacts"
        ? "contact_id"
        : moduleType.toLowerCase() === "project"
          ? "project_id"
          : "company_id"]: id,
      document: file,
      user_login_id: loginUserId,
      description: formData.description,
      document_name: formData.name,
    };

    setSubmitLoading(true);

    try {
      setLoading(true);
      addDocument(payload, {
        onSuccess: (res) => {
          if (res.status === "success") {
            toast.success(res.message || "Document added successfully");
            fetchDocument();
            reset();
            setShowModal(false);
          } else {
            toast.error(res.message || "Failed to add document");
          }
        },
        onError: (err) => {
          toast.error(err?.message || "Add document request failed");
        },
        onSettled: () => {
          setSubmitLoading(false);
        },
      });
    } catch (err) {
      console.error("Error submitting document", err);
    } finally {
      setLoading(false);
    }
  };

  const isFormSubmitting = submitLoading || isSubmitting;

  const handleOpenFile = (fileUrl) => {
    if (fileUrl) {
      window.open(fileUrl, "_blank");
    } else {
      toast.warning("No file URL found.");
    }
  };

  // Trigger delete modal
  const onDelete = (docId) => {
    setDocumentToDelete(docId);
    setShowDeleteModal(true);
  };

  const handleConfirmedDelete = async () => {
    try {
      const item = documents.find((doc) => doc.id === documentToDelete);
      if (!item) return toast.error("no record find to delete");

      const res = await DeleteRequest(
        `/${moduleType}/DeleteDocuments/${id}/${documentToDelete}/${customerId}/${loginUserId}`,
        { headers },
        extras
      );

      if (res.status !== "success" || res.status === "fail") {
        return toast.error(res.message || "Failed to delete document");
      }

      if (res.status === "success") {
        toast.success(res.message || "document deleted successfully");
        setDocuments((prevDocs) =>
          prevDocs.filter((doc) => doc.id !== documentToDelete)
        );
        await fetchDocument();
      }
    } catch (error) {
      console.error("Delete error", error);
    } finally {
      setShowDeleteModal(false);
      setDocumentToDelete(null);
    }
  };

  const visibleDocuments = useMemo(() => {
    if (!searchTerm.trim()) return documents;
    const q = searchTerm.trim().toLowerCase();
    return (documents || []).filter(
      (d) =>
        d.document_name?.toLowerCase().includes(q) ||
        d.description?.toLowerCase().includes(q)
    );
  }, [documents, searchTerm]);

  const selectedDocument = useMemo(
    () => documents.find((d) => d.id === selectedDocumentId) || null,
    [documents, selectedDocumentId]
  );

  const openCreateModal = () => {
    if (!docGrant?.isCreate) {
      toast.warning("You are not authorized for this action!");
      return;
    }
    setShowModal(true);
  };
  const initialsOf = (name) =>
    name
      ?.split(" ")
      .slice(0, 2)
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  return (
    <div className="pdc-page">
      <style>{`
        .pdc-page{ --pdc-primary:#7a3a5e; --pdc-primary-soft:rgba(122,58,94,0.08); --pdc-border:#e7e8f1; --pdc-bg:#f7f8fc; --pdc-text:#20223a; --pdc-muted:#767892; background:var(--pdc-bg); border-radius:12px; height:calc(100vh - 8rem); max-height:calc(100vh - 8rem); display:flex; flex-direction:column; font-family:inherit; overflow:hidden; }
        .pdc-topbar{ display:flex; align-items:center; justify-content:space-between; gap:16px; padding:20px 24px 14px; flex-wrap:wrap; }
        .pdc-title{ font-size:22px; font-weight:600; color:var(--pdc-text); margin:0; }
        .pdc-subtitle{ font-size:13px; color:var(--pdc-muted); margin-top:2px; }
        .pdc-topbar-actions{ display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
        .pdc-search{ position:relative; }
        .pdc-search input{ border:1px solid var(--pdc-border); border-radius:8px; padding:8px 12px 8px 34px; font-size:13px; min-width:220px; background:#fff; }
        .pdc-search input:focus{ outline:none; border-color:var(--pdc-primary); box-shadow:0 0 0 3px var(--pdc-primary-soft); }
        .pdc-search svg{ position:absolute; left:11px; top:50%; transform:translateY(-50%); color:var(--pdc-muted); font-size:13px; }
        .pdc-btn-primary{ background:var(--pdc-primary); border:none; color:#fff; font-size:13px; font-weight:500; height:36px; padding:0 16px; border-radius:8px; display:flex; align-items:center; gap:8px; white-space:nowrap; }
        .pdc-btn-primary:hover{ background:#623050; color:#fff; }
        .pdc-btn-primary:disabled{ opacity:.55; }
        .pdc-body{ display:flex; gap:7px; padding:0 24px 24px; flex:1; min-height:0; overflow:hidden; }

        .pdc-list-panel{  flex:none; display:flex; flex-direction:column; min-height:0; }
        .pdc-card{ background:#fff; border:1px solid var(--pdc-border); border-radius:12px; padding:16px; }
        .pdc-list-card{ display:flex; flex-direction:column; min-height:0; flex:1; padding:14px; }
        .pdc-list-card-head{ display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
        .pdc-list-card-head h6{ font-size:14px; font-weight:600; margin:0; color:var(--pdc-text); display:flex; align-items:center; gap:8px; }
        .pdc-list-card-head span.count{ font-size:12px; color:var(--pdc-muted); font-weight:500; }

        .pdc-history-list{ display:flex; flex-direction:column; gap:10px; flex:1; min-height:0; padding-right:2px;
          max-height: calc(-330px + 100vh);
          overflow-y: auto;
          scroll-behavior: smooth;
          width: 100%;
          scrollbar-width: thin;
          transition: scrollbar-color 0.3s; }
        .pdc-history-list::-webkit-scrollbar{ width:5px; }
        .pdc-history-list::-webkit-scrollbar-thumb{ background:#e2e8f0; border-radius:10px; }
        .pdc-history-item{ display:flex; align-items:flex-start; justify-content:space-between; gap:10px; padding:12px 14px; border-radius:12px; border:1px solid var(--pdc-border); background:#fff; cursor:pointer; box-shadow:0 1px 2px rgba(20,20,50,0.03); transition:box-shadow .15s ease, border-color .15s ease; }
        .pdc-history-item:hover{ border-color:var(--pdc-primary); box-shadow:0 4px 14px rgba(20,20,50,0.08); }
        .pdc-history-item.active{ background:var(--pdc-primary-soft); border-color:var(--pdc-primary); box-shadow:0 4px 14px rgba(122,58,94,0.12); }
        .pdc-history-item .content{ min-width:0; display:flex; gap:10px; }
        .pdc-history-item .title{ font-size:13.5px; font-weight:600; margin-bottom:4px !important; color:var(--pdc-text); }
        .pdc-history-item .meta{ font-size:11.5px; color:#6b7280; font-weight:500; }
        .pdc-file-icon{ width:38px; height:38px; border-radius:8px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .pdc-history-empty{ font-size:12.5px; color:var(--pdc-muted); text-align:center; padding:30px 8px; }

        /* ---- details panel (right) ---- */
        .pdc-detail-panel{ flex:1; min-width:0; display:flex; flex-direction:column; min-height:0; }
        .pdc-detail-card{ background:#fff; border:1px solid var(--pdc-border); border-radius:12px; flex:1; display:flex; flex-direction:column; min-height:0; overflow:hidden; position:relative; }
        .pdc-detail-header{ display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid var(--pdc-border); }
        .pdc-detail-header h6{ font-size:14px; font-weight:600; margin:0; color:var(--pdc-text); }
        .pdc-detail-body{ padding:20px; overflow-y:auto; flex:1; min-height:0; scrollbar-width:thin; transition:scrollbar-color 0.3s; }
        .pdc-detail-body::-webkit-scrollbar{ width:5px; }
        .pdc-detail-body::-webkit-scrollbar-thumb{ background:#e2e8f0; border-radius:10px; }
        .pdc-detail-doc-title{ font-weight:600; color:rgb(26,32,44); font-size:14px; word-break:break-word; }
        .pdc-detail-meta-row{ display:flex; align-items:center; gap:14px; color:var(--pdc-muted); font-size:13.5px; margin-bottom:14px; flex-wrap:wrap; }
        .pdc-detail-meta-row .item{ display:flex; align-items:center; gap:7px; font-weight:500 !important; }
        .pdc-badge-type{ display:inline-flex; align-items:center; gap:6px; font-size:11px; font-weight:600; padding:5px 12px; border-radius:20px; margin-bottom:16px; }
        .pdc-detail-section-title{ display:flex; align-items:center; gap:8px; font-size:13px; font-weight:600; color:var(--pdc-text); margin:0 0 6px; }
        .pdc-detail-desc-box{ background:var(--pdc-bg); border-left:3px solid var(--pdc-primary); border-radius:8px; padding:11px 14px; font-size:13px; color:#4a5568; margin-bottom:16px; white-space:pre-wrap; }
        .pdc-detail-file-row{ display:flex; align-items:center; justify-content:space-between; gap:10px; padding:11px 14px; border-radius:8px; background:var(--pdc-primary-soft); border:1px dashed var(--pdc-primary); margin-bottom:8px; }
        .pdc-detail-file-row .file-text{ font-size:12.5px; font-weight:500; color:var(--pdc-primary); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; display:flex; align-items:center; gap:8px; }
        .pdc-detail-file-row button{ background:#fff; border:1px solid var(--pdc-border); border-radius:6px; width:30px; height:30px; flex-shrink:0; }
        .pdc-detail-side-card{ background:#fff; border:1px solid var(--pdc-border); border-radius:12px; padding:16px; height:100%; }
        .pdc-detail-info-row{ display:flex; justify-content:space-between; align-items:center; padding:9px 0; border-bottom:1px solid var(--pdc-border); font-size:13px; }
        .pdc-detail-info-row:last-child{ border-bottom:none; }
        .pdc-detail-info-row .label{ color:var(--pdc-muted); }
        .pdc-detail-info-row .value{ color:var(--pdc-text); font-weight:600; }
        .pdc-detail-actions{ display:flex; flex-direction:column; gap:8px; margin-top:16px; }
        .pdc-detail-actions button{ width:100%; height:36px; border-radius:8px; font-size:13px; font-weight:500; display:flex; align-items:center; justify-content:center; gap:8px; }
        .pdc-detail-empty{ flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:10px; color:var(--pdc-muted); }
        .pdc-preview-img{ width:100%; max-height:260px; object-fit:contain; border-radius:8px; border:1px solid var(--pdc-border); background:var(--pdc-bg); margin-bottom:16px; }
        .pdc-preview-pdf-wrap{ width:100%; height:340px; border-radius:8px; border:1px solid var(--pdc-border); background:var(--pdc-bg); margin-bottom:16px; overflow:hidden; }
         .pmc-history-avatars{ display:flex; align-items:center; flex-shrink:0; margin-top:2px; }
        .pmc-history-avatar{ width: 28px;
    height: 28px;
    border-radius: 50%;
    background: linear-gradient(45deg, var(--pmc-primary), #9d4d7a);
    color: #fff;
    font-size: 10.5px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border: 2px solid #fff;
    box-shadow: 0 1px 3px rgba(20, 20, 50, .15); }
        .pmc-history-avatar:not(:first-child){ margin-left:-9px; }
        .pdc-preview-pdf{ width:100%; height:100%; border:none; display:block; }
      `}</style>

      {/* -------- Top Bar -------- */}
      <div className="pdc-topbar">
        <div>
          <p className="pdc-title">Documents</p>
          <p className="pdc-subtitle">
            {moduleName === "Company" ? "Company " : ""}Documents linked to this{" "}
            {moduleName?.toLowerCase() === "project" ? "project" : moduleName?.toLowerCase() || "record"}
          </p>
        </div>
        <div className="pdc-topbar-actions">
          <div className="pdc-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="pdc-btn-primary" onClick={openCreateModal}>
            <FontAwesomeIcon icon={faPlus} /> Add Document
          </button>
        </div>
      </div>

      {/* -------- Body: Document list (left) + Document Details (right) -------- */}
      {!docGrant?.isView ? (
        <div className="pdc-body">
          <UnauthViewModal tabName={"Documents"} />
        </div>
      ) : (
        <div className="pdc-body">
          {/* -------- Left: Documents list -------- */}
          <div className="pdc-list-panel col-md-3">
            <div className="pdc-card pdc-list-card">
              <div className="pdc-list-card-head">
                <h6>
                  <FontAwesomeIcon icon={faFileLines} /> Documents
                </h6>
                <span className="count">{documents?.length || 0} total</span>
              </div>

              {isLoading ? (
                <div className="d-flex justify-content-center align-items-center flex-fill">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : visibleDocuments?.length > 0 ? (
                <div className="pdc-history-list">
                  {visibleDocuments.map((d) => {
                    const typeStyle = getFileTypeStyle(d.document);
                    return (
                      <div
                        key={d.id}
                        className={`pdc-history-item ${
                          selectedDocumentId === d.id ? "active" : ""
                        }`}
                        onClick={() => setSelectedDocumentId(d.id)}
                      >
                        <div className="content">
                          <div
                            className="pdc-file-icon"
                            style={{ background: typeStyle.bg, color: typeStyle.fg }}
                          >
                            <FontAwesomeIcon icon={isPdf(d.document) ? faFilePdf : faFileImage} />
                          </div>
                          <div className="text-truncate">
                            <p className="title text-truncate">
                              {truncate(d.document_name, 26)}
                            </p>
                            <div className="meta">
                              {formatedDate(d.created_at)} • {formatedTime(d.created_at)}
                            </div>
                          </div>
                          
                        </div>
                          <div className="pmc-history-avatars">
                            <div  className="pmc-history-avatar" style= {{background:"#bd3782"}}title={d?.creator_name}>
                              {initialsOf(d?.creator_name) || "?"}
                            </div>
                        
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="pdc-history-empty">
                  {searchTerm
                    ? "No documents match your search."
                    : "This project doesn't have any documents yet."}
                  <div className="mt-2">
                    <button
                      className="pdc-btn-primary"
                      style={{ margin: "10px auto 0" }}
                      onClick={openCreateModal}
                    >
                      <FontAwesomeIcon icon={faPlus} /> Add Document
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* -------- Right: Document Details -------- */}
          <div className="pdc-detail-panel">
            <div className="pdc-detail-card">
              {!selectedDocument ? (
                <div className="pdc-detail-empty">
                  <FontAwesomeIcon icon={faFileLines} size="2x" />
                  <p className="mb-0">Select a document to view its details</p>
                </div>
              ) : (
                <>
                  <div className="pdc-detail-header">
                    <h6>Document Details</h6>
                    <Dropdown as="div" align="end">
                      <Dropdown.Toggle
                        as={Nav.Link}
                        className="p-0 m-0 bg-transparent border-0"
                        style={{ color: "var(--pdc-muted)" }}
                      >
                        <FontAwesomeIcon icon={faEllipsisVertical} />
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="dropdown-menu-end" style={{ minWidth: "11rem" }}>
                        <Dropdown.Item
                          className="d-flex align-items-center gap-2"
                          onClick={() => {
                            if (!docGrant?.isView) {
                              toast.warning("You are not authorized for this action!");
                              return;
                            }
                            handleDownloadFile({
                              docPath: selectedDocument.document,
                              headers,
                              extras,
                            });
                          }}
                        >
                          <FontAwesomeIcon icon={faDownload} /> Download
                        </Dropdown.Item>
                        <Dropdown.Item
                          className="d-flex align-items-center gap-2 text-danger"
                          onClick={() => {
                            if (!docGrant?.isDelete) {
                              toast.warning("You are not authorized for this action!");
                              return;
                            }
                            onDelete(selectedDocument.id);
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} /> Delete
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>

                  <div className="pdc-detail-body">
                    <div className="row g-4">
                      <div className="col-lg-7">
                        {isPdf(selectedDocument.document) ? (
                          <div className="pdc-preview-pdf-wrap">
                            <iframe
                              key={selectedDocument.id}
                              className="pdc-preview-pdf"
                              src={`${imageURL}${selectedDocument.document}#toolbar=0`}
                              title={selectedDocument.document_name}
                            />
                          </div>
                        ) : (
                          <img
                            className="pdc-preview-img"
                            src={`${imageURL}${selectedDocument.document}`}
                            alt={selectedDocument.document_name}
                          />
                        )}

                        <h3 className="pdc-detail-doc-title">
                          {selectedDocument.document_name}
                        </h3>

                        <div className="pdc-detail-meta-row">
                          <span className="item">
                            <FontAwesomeIcon icon={faCalendarDays} />
                            {formatedDate(selectedDocument.created_at)}
                          </span>
                          <span className="item">
                            <FontAwesomeIcon icon={faClock} />
                            {formatedTime(selectedDocument.created_at)}
                          </span>
                        </div>

                        <span
                          className="pdc-badge-type"
                          style={{
                            background: getFileTypeStyle(selectedDocument.document).bg,
                            color: getFileTypeStyle(selectedDocument.document).fg,
                          }}
                        >
                          <FontAwesomeIcon
                            icon={isPdf(selectedDocument.document) ? faFilePdf : faFileImage}
                          />
                          {getFileTypeStyle(selectedDocument.document).label}
                        </span>

                        <p className="pdc-detail-section-title">Description</p>
                        <div className="pdc-detail-desc-box">
                          {selectedDocument.description || "No description provided."}
                        </div>

                        <p className="pdc-detail-section-title">File</p>
                        <div className="pdc-detail-file-row">
                          <span className="file-text">
                            <FontAwesomeIcon
                              icon={isPdf(selectedDocument.document) ? faFilePdf : faFileImage}
                            />
                            {selectedDocument.document?.split("/").pop()}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              handleOpenFile(`${imageURL}${selectedDocument.document}`)
                            }
                            title="Open file"
                          >
                            <FontAwesomeIcon icon={faFileLines} />
                          </button>
                        </div>
                      </div>

                      <div className="col-lg-5">
                        <div className="pdc-detail-side-card">
                          <p className="pdc-detail-section-title mb-2">Details</p>
                           <div className="pdc-detail-info-row">
                            <span className="label">Created by</span>
                            <span className="fw-600 fs-14">
                              
                              {selectedDocument?.creator_name}
                            </span>
                          </div>
                          <div className="pdc-detail-info-row">
                            <span className="label">File Type</span>
                            <span className="fw-600 fs-14">
                              {getFileTypeStyle(selectedDocument.document).label}
                            </span>
                          </div>

                          <div className="pdc-detail-info-row">
                            <span className="label">Uploaded On</span>
                            <span className="fw-600 fs-14">
                              {formatedDate(selectedDocument.created_at)}
                            </span>
                          </div>
                          <div className="pdc-detail-info-row">
                            <span className="label">Uploaded At</span>
                            <span className="fw-600 fs-14">
                              {formatedTime(selectedDocument.created_at)}
                            </span>
                          </div>

                          <div className="pdc-detail-actions">
                            <button
                              type="button"
                              className="pdc-btn-primary"
                              style={{ background: "var(--pdc-primary)" }}
                              onClick={() => {
                                if (!docGrant?.isView) {
                                  toast.warning("You are not authorized for this action!");
                                  return;
                                }
                                handleDownloadFile({
                                  docPath: selectedDocument.document,
                                  headers,
                                  extras,
                                });
                              }}
                            >
                              <FontAwesomeIcon icon={faDownload} /> Download
                            </button>
                            <button
                              type="button"
                              style={{
                                background: "#fff",
                                border: "1px solid var(--pdc-border)",
                                color: "#dc3545",
                              }}
                              onClick={() => {
                                if (!docGrant?.isDelete) {
                                  toast.warning("You are not authorized for this action!");
                                  return;
                                }
                                onDelete(selectedDocument.id);
                              }}
                            >
                              <FontAwesomeIcon icon={faTrash} /> Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Document Modal */}
      <Modal
        show={showModal}
        onHide={() => !submitLoading && setShowModal(false)}
        centered
        backdrop={submitLoading ? "static" : true}
        keyboard={!submitLoading}
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ textTransform: "capitalize" }}>
            Add {moduleName} Document
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data" className="form-container">
            {/* Document Name */}
            <Form.Group className="mb-3 focus-z-index position-relative" controlId="formName">
              <Form.Label>
                Document Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter document name"
                {...register("name")}
                isInvalid={!!errors.name}
                disabled={submitLoading}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name?.message}
              </Form.Control.Feedback>
            </Form.Group>

            {/* File Upload */}
            <Form.Group className="mb-3 focus-z-index position-relative mt-1" controlId="formFile">
              <Form.Control
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx,.doc,.docx"
                {...register("file")}
                isInvalid={!!errors.file}
                disabled={submitLoading}
              />
              <p
                style={{
                  color: "#a5a5a5",
                  fontSize: "11px",
                  fontWeight: 400,
                }}
              >
                Accepted file types are .jpg, .png, .pdf, .jpeg, max file size 5MB
              </p>
              <Form.Control.Feedback type="invalid">
                {errors.file?.message}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Description */}
            <Form.Group className="mb-3 focus-z-index position-relative" controlId="formDescription">
              <Form.Label>
                Description <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Write a short description..."
                {...register("description")}
                isInvalid={!!errors.description}
                disabled={submitLoading}
              />
              <Form.Control.Feedback type="invalid">
                {errors.description?.message}
              </Form.Control.Feedback>
            </Form.Group>
            <hr style={{ marginBottom: "10px" }} />
            {/* Action Buttons */}
            <div className="mb-2 d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={onClose}
                className="btn btn-default-outline btn-sm"
                disabled={submitLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isFormSubmitting} className="btn btn-default btn-sm">
                {submitLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    Submitting...
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmedDelete}
        isLoading={loading}
        message={"Are you sure you want to permanently delete this document?"}
      />
    </div>
  );
};

export default ProjectDocument;